/**
 * The needs-attention reason codes, and what this form can do about each.
 *
 * **UI-only.** `SalesforceOpportunityResolveRequest` has no reason-code field
 * and its `reason` is documented as free prose, "in their own words", so the
 * selection is never sent, and is not folded into `reason` either: a prefix in
 * an editable textarea is structure in appearance only, and the code is
 * already recoverable server-side by `opportunityId`.
 *
 * What it buys is routing. Without it, an operator holding a case this form
 * cannot resolve fills it in, gets a 202, and learns from Slack that nothing
 * happened.
 *
 * **Guidance, not authority.** The copy is a summary of situations that keep
 * changing as dependencies ship. The runbook is the source of truth, every
 * panel links to it, and it is the same URL the needs-attention message
 * already carries — see `RUNBOOK_URL`.
 */

/**
 * Where the needs-attention message sends the reader, via `RUNBOOK_URL` in
 * `SalesforceOrchestrationService`. The two are kept in step deliberately, so
 * the form and the Slack message never send someone to different places.
 *
 * APP-2451 wrote the runbook and moved the operational answers out of the
 * design document, which now covers what the system decides and why. Pointing
 * here rather than there is what makes the guidance below a summary of
 * something rather than the only copy of it.
 */
export const RUNBOOK_URL =
  'https://linear.app/hivemq/document/needs-attention-runbook-what-to-do-by-reason-code-0bff75376f74'

/**
 * The nine codes an operator can see, per APP-2451.
 *
 * Internal reason strings are deliberately absent. `malformed-account-id`,
 * `lookup-failed` and APP-2450's `auth0-lookup-failed` are all internal: they
 * distinguish *which read failed* for telemetry, they surface to CS as
 * `delivery-failed`, and APP-2426 constraint 2 forbids them from appearing in
 * a message body at all. Listing one here would offer the operator a string
 * they can never have seen.
 *
 * `duplicate` is absent for the same reason: it is designed but not built,
 * `NeedsAttentionReason` has nine codes and it is not one of them, so it can
 * never appear in a message either. APP-2145 adds it back with the code that
 * makes it real.
 *
 * Order is the runbook's, not alphabetical: the operator is scanning for a
 * string they can already see in Slack.
 */
export const REASON_CODES = [
  'multi-link',
  'multi-candidate',
  'not-member',
  'no-account',
  'all-filtered',
  'multi-auth0',
  'delivery-failed',
  'orgid-invalid',
  'resolution-conflict',
] as const

export type ReasonCode = (typeof REASON_CODES)[number]

/** The dropdown's default and its cleared state. Not a code: "I did not say". */
export const NO_REASON_CODE = ''

export type ReasonCodeSelection = ReasonCode | typeof NO_REASON_CODE

/**
 * No code steps the form aside entirely any more.
 *
 * There were two. `multi-auth0` became advisory when APP-2440 taught the
 * resolve endpoint to deliver one into a named organisation, and `duplicate`
 * left with the dropdown because the platform cannot produce it. APP-2145
 * brings the concept back with `duplicate`: an opportunity already delivered
 * has nothing for a resolution to do.
 */

/**
 * Codes where this form *may* be the answer, but often is not.
 *
 * The distinction is real rather than hedging. On `not-member`, `no-account`,
 * `all-filtered` and `multi-link` the usual fix happens outside this flow —
 * the org owner invites the person, their IdP adds them under SSO, or the
 * Salesforce link is re-pointed on the accounts screen — and the opportunity
 * is then re-sent from Salesforce. This form is right when the answer is a
 * *different or new organisation*, which is the create route APP-2426
 * constraint 4 names, and on `multi-link` when the answer is simply which of
 * the linked organisations to use.
 *
 * `delivery-failed` is different again: its route depends on how the case
 * reached CS. One from the Salesforce ingest is re-sent from Salesforce; one
 * from a previous resolution is re-submitted here, and sending that operator
 * back to Salesforce would discard the choice they already made.
 *
 * `multi-auth0` is different for a third reason: the limit is the endpoint's,
 * not the workflow's. APP-2440 lifted its stop only where the operator reuses
 * an existing account in a named organisation, so answering no to both
 * questions with an org id delivers, while asking for a new organisation or a
 * new account still stops on the same code. The form is usable, and which
 * route the operator picks decides whether it works.
 *
 * So all six warn and leave the form open. Closing it would be wrong for the
 * branch where a resolution is exactly right.
 */
export const ADVISORY_CODES = {
  'multi-link': true,
  'not-member': true,
  'no-account': true,
  'all-filtered': true,
  'delivery-failed': true,
  'multi-auth0': true,
} as const satisfies Partial<Record<ReasonCode, true>>

export type AdvisoryCode = keyof typeof ADVISORY_CODES

/**
 * True when the form stays usable but the operator needs a caution first.
 *
 * Three codes are resolvable with no caveat at all: `multi-candidate` needs a
 * choice only a human can express, and `orgid-invalid` and
 * `resolution-conflict` are resolve-only by construction, fixed by
 * re-submitting a corrected resolution.
 */
export function isAdvisory(
  selection: ReasonCodeSelection,
): selection is AdvisoryCode {
  return selection !== NO_REASON_CODE && selection in ADVISORY_CODES
}

/** Narrows an arbitrary string, so a stale value cannot become a code. */
export function isReasonCode(value: string): value is ReasonCode {
  return (REASON_CODES as readonly string[]).includes(value)
}
